export type MissingField = 'firstName' | 'lastName' | 'company';

export interface ProfileCheckResult {
  isComplete: boolean;
  missingFields: MissingField[];
  existingData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
  };
}

/**
 * Check if a user's profile has all required fields for SMB signup.
 *
 * Required fields:
 * - firstName (from user.first_name or user.profile.first_name)
 * - lastName (from user.last_name or user.profile.last_name)
 * - company (from group.name)
 *
 * Supports two data structures:
 * 1. Direct: user.first_name (from OTP verify response)
 * 2. Nested: user.profile.first_name (from viewer query)
 *
 * @param user - User object with profile fields
 * @param group - User's primary group (contains company name)
 * @returns ProfileCheckResult with completeness status and any missing fields
 */
export function checkProfileCompleteness(
  user: {
    first_name?: string | null;
    last_name?: string | null;
    email?: { address?: string } | null;
    profile?: {
      first_name?: string | null;
      last_name?: string | null;
      emails?: Array<{ address?: string }> | null;
    } | null;
  },
  group?: { name?: string | null } | null
): ProfileCheckResult {
  const missingFields: MissingField[] = [];

  // Support both direct (user.first_name) and nested (user.profile.first_name) structures
  const firstName = user.first_name || user.profile?.first_name;
  const lastName = user.last_name || user.profile?.last_name;
  const email = user.email?.address || user.profile?.emails?.[0]?.address;

  if (!firstName?.trim()) missingFields.push('firstName');
  if (!lastName?.trim()) missingFields.push('lastName');
  if (!group?.name?.trim()) missingFields.push('company');

  return {
    isComplete: missingFields.length === 0,
    missingFields,
    existingData: {
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      company: group?.name || '',
    },
  };
}
